import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Save, ArrowUp, ArrowDown, Wand2 } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

function SortableRow({ item, index, total, onMoveUp, onMoveDown, onToggleInclude, onToggleActive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef} style={style} {...attributes}
      data-testid={`seq-item-${index}`}
      className={`grid grid-cols-[auto_48px_1fr_auto_auto_auto] items-center gap-4 bg-white border border-border rounded-xl px-4 py-3 ${
        item.include_in_sequence === false ? "opacity-60" : ""
      }`}
    >
      <button {...listeners} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-primary">
        <GripVertical />
      </button>
      <div className="w-10 h-10 rounded-md bg-primary/10 text-primary font-serif text-xl flex items-center justify-center">
        {index + 1}
      </div>
      <div className="min-w-0">
        <p className="font-medium truncate">{item.entity || <span className="text-slate-400">—</span>}</p>
        <p className="text-sm text-slate-500 truncate">
          {item.program || "—"} · {item.specialization || "—"}
        </p>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Include</span>
        <Switch
          data-testid={`seq-include-${index}`}
          checked={item.include_in_sequence !== false}
          onCheckedChange={(v) => onToggleInclude(item.id, v)}
        />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Active</span>
        <Switch
          data-testid={`seq-active-${index}`}
          checked={item.is_active !== false}
          onCheckedChange={(v) => onToggleActive(item.id, v)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Button size="icon" variant="ghost" disabled={index === 0} onClick={() => onMoveUp(index)} data-testid={`seq-up-${index}`}>
          <ArrowUp size={14} />
        </Button>
        <Button size="icon" variant="ghost" disabled={index === total - 1} onClick={() => onMoveDown(index)} data-testid={`seq-down-${index}`}>
          <ArrowDown size={14} />
        </Button>
      </div>
    </div>
  );
}

export default function SequenceConfig() {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = async () => {
    try {
      const r = await api.get("/sequence/config");
      setItems(r.data);
      setDirty(false);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    setItems(arrayMove(items, oldIdx, newIdx));
    setDirty(true);
  };

  const moveUp = (idx) => { if (idx > 0) { setItems(arrayMove(items, idx, idx - 1)); setDirty(true); } };
  const moveDown = (idx) => { if (idx < items.length - 1) { setItems(arrayMove(items, idx, idx + 1)); setDirty(true); } };

  const toggleInclude = async (id, v) => {
    try {
      await api.patch(`/sequence/config/${id}`, { include_in_sequence: v });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, include_in_sequence: v } : i));
      toast.success(v ? "Included in ceremony" : "Excluded from ceremony");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleActive = async (id, v) => {
    try {
      await api.patch(`/sequence/config/${id}`, { is_active: v });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, is_active: v } : i));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sequence/config/reorder", { ordered_ids: items.map((i) => i.id) });
      setItems(r.data);
      setDirty(false);
      toast.success("Sequence saved");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sequence/generate");
      toast.success(`Generated ${r.data.added} entries from Student Categories`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="sequence-page">
      <PageHeader
        title="Sequence Configuration"
        subtitle="Auto-generated from committed Student Categories. Drag to reorder or use the arrows. Toggle Include to add / remove a category from the live ceremony."
        actions={
          <div className="flex gap-2">
            <Button
              data-testid="save-sequence-btn"
              onClick={save} disabled={!dirty || busy}
              className="bg-primary hover:bg-primary/90"
            >
              <Save size={16} className="mr-2" /> Save Order
            </Button>
          </div>
        }
      />

      {items.length === 0 ? (
        <div className="max-w-2xl bg-white border border-dashed border-border rounded-xl p-10 text-center">
          <p className="kicker">No sequence yet</p>
          <h2 className="font-serif text-2xl mt-3">Generate Sequence from Categories</h2>
          <p className="text-slate-500 mt-3">
            Upload master data first, then click below to auto-generate the ceremony sequence from the committed Student Categories.
          </p>
          <Button
            onClick={generate}
            className="mt-6 bg-primary hover:bg-primary/90"
            data-testid="generate-sequence-btn"
            disabled={busy}
          >
            <Wand2 size={16} className="mr-2" /> Generate Sequence
          </Button>
        </div>
      ) : (
        <div className="max-w-5xl">
          <p className="text-xs text-slate-500 mb-3">
            Note: <b>Include in Ceremony</b> is presented at the category level per the spec, but student-level enrolment (per registration) is authoritative during check-in.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <SortableRow
                    key={it.id} item={it} index={idx} total={items.length}
                    onMoveUp={moveUp} onMoveDown={moveDown}
                    onToggleInclude={toggleInclude} onToggleActive={toggleActive}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
